<template>
  <v-container>
    <v-dialog
      :value="value"
      @input="$emit('input', $event.target.value)"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card color="primary">
        <v-toolbar color="primary">
          <v-toolbar-title>
            <h3>{{ isEdit ? "Editing Quality Audit" : "New Quality Audit" }}</h3>
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            @click="
              $emit('input', false);
              reset();
            "
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-stepper v-model="currentStep" vertical class="elevation-0" v-if="value">
          <v-stepper-items>
            <v-stepper-step step="1" :complete="currentStep > 1" editable>Choose a Department</v-stepper-step>
            <v-stepper-content step="1">
              <v-form
                @submit.prevent="
                  body.category
                    ? currentStep++
                    : alert('You must select a department!')
                "
              >
                <v-card-text>
                  <v-select
                    @change="currentStep++"
                    v-model="body.category"
                    :items="categories"
                    color="secondary"
                    box
                    label="Select A Section"
                  />
                  <v-text-field
                    v-model="otherCategory"
                    v-if="body.category === 'Other'"
                    box
                    color="secondary"
                    label="Other Section"
                  />
                </v-card-text>
                <v-card-actions v-if="value">
                  <v-spacer />
                  <v-btn
                    @click="
                      $emit('input', false);
                      reset();
                    "
                    flat
                  >Cancel</v-btn>
                  <v-btn type="submit" flat color="secondary lighten-2">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>
            <v-stepper-step
              step="2"
              :complete="currentStep > 2"
              :editable="body.category != ''"
            >Choose an Operator</v-stepper-step>
            <v-stepper-content step="2">
              <v-form
                @submit.prevent="
                  body.operator
                    ? currentStep++
                    : alert('You must select an operator!')
                "
              >
                <v-card-text>
                  <UserSearch
                    v-if="value && currentStep > 1"
                    @employee-selected="employeeSelected"
                    :user="
                      isEdit
                        ? {
                            displayName: body.operator.name,
                            employeeID: body.operator.badgeID
                          }
                        : null
                    "
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn type="submit" flat color="secondary lighten-2">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>
            <v-stepper-step step="3" :complete="currentStep > 3" editable>SKU and Lot Number</v-stepper-step>
            <v-stepper-content step="3">
              <v-form>
                <v-card-text>
                  <v-text-field label="SKU" v-model="body.sku" box color="secondary" />
                  <v-text-field label="Lot Number" v-model="body.lotNumber" box color="secondary" />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn @click="currentStep++" flat color="secondary lighten-2">Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>
            <v-stepper-step
              step="4"
              :complete="currentStep > 4"
              :editable="body.category != ''"
            >Questionnaire</v-stepper-step>
            <v-stepper-content step="4">
              <v-form @submit.prevent="currentStep++">
                <h2 v-if="!body.category" class="text-xs-center">You Must Select A Department</h2>
                <span v-if="body.category === 'Other'">
                  <h2 class="text-xs-center">Other Department Chosen, No Questions</h2>
                </span>
                <span v-else v-for="(question, index) in questions.questions" :key="index">
                  <YesOrNo
                    v-if="body.category"
                    :question="question"
                    :alternate="index % 2 ? false : true"
                    :fillAnswer="
                      isEdit && (body.questionnaire.length === edit.questionnaire.length) ? edit.questionnaire[index].responses : null
                    "
                    @data="mapQuestions"
                  />
                </span>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    type="submit"
                    flat
                    color="secondary lighten-2"
                    :disabled="disableLast()"
                  >Continue</v-btn>
                </v-card-actions>
              </v-form>
            </v-stepper-content>
            <v-stepper-step
              step="5"
              :complete="currentStep > 5"
              :editable="!disableLast()"
            >Additional Remarks</v-stepper-step>
            <v-stepper-content step="5">
              <v-text-field
                label="Use this field to add any additional comments to this audit"
                color="secondary"
                box
                v-model="body.remarks"
              />
              <v-card-actions>
                <v-spacer />
                <v-btn @click="createOrEdit()" flat color="secondary lighten-2">Submit Audit</v-btn>
              </v-card-actions>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import UserSearch from "@/components/app/UserSearch";
import YesOrNo from "../../app/YesOrNo";
import { detailedDiff } from "deep-object-diff";

export default {
  name: "Create",

  props: ["value"],

  components: {
    UserSearch,
    YesOrNo
  },

  data() {
    return {
      currentStep: 1,
      body: {
        category: "",
        sku: "",
        lotNumber: "",
        questionnaire: []
      },
      otherCategory: "",
      isEdit: false
    };
  },

  computed: {
    ...mapGetters({
      categories: "audits/getCategories",
      questions: "audits/getQuestions",
      user: "app/getUser",
      edit: "audits/getEditAudit"
    }),
    category() {
      return this.body.category;
    }
  },

  watch: {
    category(val) {
      setTimeout(() => {
        this.$store.dispatch("audits/getQuestions", {
          module: "quality",
          category: val
        });
      }, 350);
    },
    questions(val) {
      this.body.questionnaire = [];
      if (this.category !== "Other" && this.category && val) {
        this.body.questionnaire = val.questions.map(question => {
          return { question: question };
        });
      }
    },
    edit(val) {
      if (val._id) {
        this.isEdit = true;
        this.body = {
          category: this.categories.includes(val.category)
            ? val.category
            : "Other",
          auditor: val.auditor,
          operator: val.operator,
          dateCreated: val.dateCreated,
          sku: val.sku,
          lotNumber: val.lotNumber,
          questionnaire: val.questionnaire,
          remarks: val.remarks
        };

        this.otherCategory = !this.categories.includes(val.category)
          ? val.category
          : "";
      }
    }
  },

  mounted() {
    this.$store.dispatch("audits/getCategories", { module: "quality" });
  },

  methods: {
    reset() {
      this.body = {
        category: "",
        sku: "",
        lotNumber: "",
        questionnaire: []
      };
      this.currentStep = 1;
      this.isEdit = false;
      this.$emit("input", false);

      this.$store.commit("audits/setQuestions", []);
      this.$store.commit("audits/setEditAudit", []);
      this.$store.dispatch("audits/getAllAudits", {
        rowsPerPage: 10,
        sortBy: "dateCreated",
        descending: true,
        page: 1,
        from: null,
        to: null,
        search: null,
        module: "quality"
      });
    },
    employeeSelected(e) {
      let name = null;
      let badgeID = null;
      try {
        name = e.split("(")[0].trim();
        badgeID = e.match(/\d+/)[0];
      } catch {
        badgeID = "";
      }
      this.body.operator = {
        name,
        badgeID
      };
    },
    alert(msg) {
      this.$store.dispatch("app/setSnackbar", {
        color: "red",
        text: msg
      });
    },
    mapQuestions(data) {
      this.body.questionnaire.filter(question => {
        if (question.question === data.question) {
          question.responses = data.val;
        }
      });

      this.disableLast();
    },
    createOrEdit() {
      this.body.auditor = {
        name: this.user.displayName,
        badgeID: this.user.employeeID
      };
      if (this.body.category === "Other") {
        this.body.category = this.otherCategory;
      }
      if (this.body.category && this.body.operator) {
        if (!this.isEdit) {
          this.$store.dispatch("audits/create", {
            module: "quality",
            body: this.body
          });
        } else {
          this.body.editedBy = this.user.displayName;
          this.body.changed = this.getChanged();
          this.$store.dispatch("audits/update", {
            module: "quality",
            body: this.body,
            id: this.edit._id
          });
        }
        this.$store.dispatch("audits/getAllAudits", {
          rowsPerPage: 10,
          sortBy: "dateCreated",
          descending: true,
          page: 1,
          from: null,
          to: null,
          search: null,
          module: "quality"
        });

        this.reset();
      } else {
        this.alert("Confirm that you filled out all required fields.");
      }
    },
    disableLast() {
      let disabled = this.body.questionnaire.map(question => {
        if (!question.responses) {
          return 1;
        } else if (question.responses.no && !question.responses.comments) {
          return 1;
        } else if (!question.responses.no && !question.responses.yes) {
          return 1;
        } else {
          return 0;
        }
      });
      if (
        disabled.includes(1) ||
        (this.body.category !== "Other" && !disabled.length)
      ) {
        return true;
      } else {
        return false;
      }
    },
    getChanged() {
      let { edit, body } = this;
      let oldObj = {
        auditor: edit.auditor,
        category: edit.category,
        lotNumber: edit.lotNumber,
        operator: edit.operator,
        questionnaire: edit.questionnaire,
        remarks: edit.remarks,
        sku: edit.sku
      };
      let newObj = {
        auditor: body.auditor,
        category: body.category,
        lotNumber: body.lotNumber,
        operator: body.operator,
        questionnaire: body.questionnaire,
        remarks: body.remarks,
        sku: body.sku
      };

      return detailedDiff(oldObj, newObj);
    }
  }
};
</script>

<style></style>
